(() => {
  "use strict";

  const PRODUCT_ROOT_SELECTOR =
    "[data-ag-sami-product-page], [data-ag-sami-product-card]";
  const TAX_RATE = 0.07;
  const currency =
    window.Shopify?.currency?.active ||
    document.documentElement.getAttribute("data-currency") ||
    "EUR";
  const locale = document.documentElement.lang || "de-DE";
  const moneyFormatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  });

  let syncQueued = false;

  function formatMoney(cents) {
    return moneyFormatter.format(cents / 100);
  }

  function parseLocalizedNumber(rawValue) {
    let value = String(rawValue || "")
      .trim()
      .replace(/\s/g, "")
      .replace(/[^\d,.'-]/g, "")
      .replace(/'/g, "");

    if (!value || value === "-") return null;

    const commaIndex = value.lastIndexOf(",");
    const dotIndex = value.lastIndexOf(".");
    const decimalIndex = Math.max(commaIndex, dotIndex);

    if (decimalIndex >= 0) {
      const decimals = value.length - decimalIndex - 1;

      if (decimals === 1 || decimals === 2) {
        const integerPart = value.slice(0, decimalIndex).replace(/[.,]/g, "");
        const decimalPart = value.slice(decimalIndex + 1);
        value = `${integerPart}.${decimalPart}`;
      } else {
        value = value.replace(/[.,]/g, "");
      }
    }

    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function readMoneyCents(element) {
    if (!element) return null;

    const renderedValue = element.matches("[data-ag-sami-value]")
      ? element
      : element.querySelector("[data-ag-sami-value]");
    const exactValue = renderedValue?.getAttribute("data-ag-sami-value");
    const exactNumber = parseLocalizedNumber(exactValue);

    if (exactNumber !== null) {
      return Math.round(exactNumber * 100);
    }

    const candidates = (element.textContent || "").match(/-?\d[\d\s.,']*/g) || [];
    const cents = candidates
      .map(parseLocalizedNumber)
      .filter((number) => number !== null && number >= 0)
      .map((number) => Math.round(number * 100));

    if (cents.length) {
      return Math.min(...cents);
    }

    const fallback = Number.parseInt(element.dataset.agBaseCents || "", 10);
    return Number.isFinite(fallback) ? fallback : null;
  }

  function setText(element, value) {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  }

  function formatSamiOutput(priceHook, cents) {
    const renderedValue = priceHook?.querySelector("[data-ag-sami-value]");
    if (!renderedValue || cents === null) return;

    setText(renderedValue, formatMoney(cents));
  }

  function syncComparePrice(root, currentCents) {
    const compare = root.querySelector("[data-ag-wholesale-compare]");
    if (!compare || currentCents === null) return;

    const baseCents = Number.parseInt(root.dataset.agBaseCents || "", 10);
    const retailCents = Number.parseInt(compare.dataset.agRetailCents || "", 10);
    let compareCents = null;

    if (Number.isFinite(baseCents) && currentCents < baseCents) {
      compareCents = baseCents;
    } else if (
      Number.isFinite(retailCents) &&
      Number.isFinite(baseCents) &&
      retailCents > baseCents
    ) {
      compareCents = retailCents;
    }

    compare.hidden = compareCents === null;
    if (compareCents !== null) {
      setText(compare, formatMoney(compareCents));
    }
  }

  function syncProductPrice(root) {
    const isProductPage = root.matches("[data-ag-sami-product-page]");
    const priceHook = root.querySelector(
      isProductPage
        ? "[data-ag-sami-product-price]"
        : "[data-ag-sami-collection-price]"
    );
    const currentCents = readMoneyCents(priceHook);

    if (currentCents === null) return;

    formatSamiOutput(priceHook, currentCents);
    priceHook.dataset.agCurrentCents = String(currentCents);

    const piecesPerCarton = Number.parseFloat(
      root.dataset.agPiecesPerCarton || "0"
    );
    const piecePrice = root.querySelector("[data-ag-piece-price]");
    const unitWrapper = piecePrice?.closest(".ag-price-row__unit");

    if (piecePrice && piecesPerCarton > 0) {
      setText(piecePrice, formatMoney(Math.round(currentCents / piecesPerCarton)));
      unitWrapper?.classList.remove("is-unavailable");
    } else {
      setText(piecePrice, "");
      unitWrapper?.classList.add("is-unavailable");
    }

    syncComparePrice(root, currentCents);
  }

  function readCartUnitCents(row) {
    const unitPrice = row.querySelector("[data-ag-cart-unit-price]");
    const parsedCents = readMoneyCents(unitPrice);
    const baseCents = Number.parseInt(unitPrice?.dataset.agBaseCents || "", 10);
    const lastValidCents = Number.parseInt(
      unitPrice?.dataset.agLastValidCents || "",
      10
    );
    const upperBound = Number.isFinite(baseCents)
      ? Math.max(baseCents * 10, 100000)
      : 100000000;

    let unitCents = parsedCents;
    if (
      !Number.isSafeInteger(unitCents) ||
      unitCents < 0 ||
      unitCents > upperBound
    ) {
      unitCents = Number.isSafeInteger(lastValidCents)
        ? lastValidCents
        : baseCents;
    }

    if (Number.isSafeInteger(unitCents) && unitPrice) {
      unitPrice.dataset.agLastValidCents = String(unitCents);
      formatSamiOutput(unitPrice, unitCents);
    }

    return Number.isSafeInteger(unitCents) ? unitCents : null;
  }

  function readCartQuantity(row) {
    const input = row.querySelector("[samitaWS-cart-line-item-qty]");
    const quantity = Number.parseInt(input?.value || "0", 10);

    if (!Number.isSafeInteger(quantity) || quantity < 0) return 0;
    return Math.min(quantity, 1000000);
  }

  function syncCart() {
    const cartPage = document.querySelector("[data-ag-cart-page]");
    if (!cartPage) return;

    let netTotalCents = 0;
    let hasValidLines = false;

    cartPage.querySelectorAll("[data-ag-cart-line]").forEach((row) => {
      const unitCents = readCartUnitCents(row);
      const quantity = readCartQuantity(row);
      if (unitCents === null) return;

      const lineNetCents = unitCents * quantity;
      if (!Number.isSafeInteger(lineNetCents)) return;

      hasValidLines = true;
      netTotalCents += lineNetCents;
      const taxCents = Math.round(lineNetCents * TAX_RATE);
      const grossCents = lineNetCents + taxCents;
      setText(
        row.querySelector("[data-ag-cart-line-tax] > span"),
        formatMoney(taxCents)
      );
      setText(row.querySelector("[data-ag-cart-line-gross]"), formatMoney(grossCents));
    });

    if (hasValidLines && Number.isSafeInteger(netTotalCents)) {
      setText(cartPage.querySelector("[data-ag-cart-net]"), formatMoney(netTotalCents));
      const taxCents = Math.round(netTotalCents * TAX_RATE);
      setText(cartPage.querySelector("[data-ag-cart-tax]"), formatMoney(taxCents));
      setText(
        cartPage.querySelector("[data-ag-cart-gross]"),
        formatMoney(netTotalCents + taxCents)
      );
    }

    cartPage.classList.remove("is-sami-updating");
    cartPage.removeAttribute("aria-busy");
  }

  function syncAll() {
    document.querySelectorAll(PRODUCT_ROOT_SELECTOR).forEach(syncProductPrice);
    syncCart();
  }

  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;

    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncAll();
    });
  }

  function init() {
    syncAll();

    const observer = new MutationObserver(scheduleSync);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        "class",
        "samitaWS-product-main-price",
        "samitaWS-cart-product-main-price",
        "data-ag-sami-value",
      ],
    });

    document.addEventListener("change", (event) => {
      if (
        event.target.matches(
          "[samitaWS-product-quantity], [samitaWS-cart-line-item-qty]"
        )
      ) {
        event.target
          .closest("[data-ag-cart-page]")
          ?.classList.add("is-sami-updating");
        event.target
          .closest("[data-ag-cart-page]")
          ?.setAttribute("aria-busy", "true");
        scheduleSync();
      }
    });

    document.addEventListener("shopify:section:load", scheduleSync);
    document.addEventListener("samitaWS.scripts.loaded", scheduleSync);
    window.addEventListener("samitaWSRenderWholesaleCompleted", scheduleSync);
    window.addEventListener("pageshow", scheduleSync);

    [250, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(scheduleSync, delay);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
